import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import useModuleCatalog from './useModuleCatalog';

// Chave versionada (handoff da home §4). O sufixo por usuário evita que o
// histórico de um operador vaze para outro na mesma máquina.
const STORAGE_PREFIX = 'mwa.home.recentes.v1';

const MAX_STORED = 8;
const MAX_SHOWN = 5;

const storageKeyFor = (userId) =>
   userId ? `${STORAGE_PREFIX}:${userId}` : STORAGE_PREFIX;

/**
 * Só `{ href, ts }` é persistido — `label` e `icon` são re-derivados do
 * catálogo na leitura.
 *
 * Divergência deliberada do §4 (que sugeria persistir `{href,label,icon,ts}`):
 * `icon` é um componente React, não serializável. Derivar da árvore na leitura
 * também entrega de graça a revalidação de permissão que o próprio §4 exige —
 * item que saiu do catálogo (permissão revogada, rota removida) simplesmente
 * não encontra par e é descartado.
 */
function readStored(userId) {
   try {
      const raw = window.localStorage.getItem(storageKeyFor(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((e) => e && typeof e.href === 'string');
   } catch {
      // localStorage indisponível (modo privado//quota) ou JSON corrompido.
      // Histórico é conveniência: falhar aqui não pode quebrar a navegação.
      return [];
   }
}

function writeStored(userId, entries) {
   try {
      window.localStorage.setItem(
         storageKeyFor(userId),
         JSON.stringify(entries)
      );
   } catch {
      // idem readStored — degrada para "sem histórico", nunca lança.
   }
}

/**
 * Registra a visita à rota atual quando ela corresponde a um item do catálogo.
 *
 * Montado no AppShell (não na home): o §4 pede captura no router, para que o
 * histórico reflita a navegação real do usuário e não só o que ele abriu a
 * partir da home.
 */
export function useRecordScreenVisit() {
   const { pathname } = useLocation();
   const userId = useSelector((state) => state.auth.user?.id);
   const { flatItems } = useModuleCatalog();

   const knownHrefs = useMemo(
      () => new Set(flatItems.map((i) => i.to)),
      [flatItems]
   );

   useEffect(() => {
      if (!userId) return;
      if (!knownHrefs.has(pathname)) return;

      const previous = readStored(userId).filter((e) => e.href !== pathname);
      const next = [{ href: pathname, ts: Date.now() }, ...previous].slice(
         0,
         MAX_STORED
      );

      writeStored(userId, next);
   }, [pathname, userId, knownHrefs]);
}

/**
 * Lê o histórico, revalida contra a árvore de permissões e devolve os itens
 * mais recentes já hidratados com label/ícone/caminho.
 *
 * @returns {{ recents: Array, clearRecents: () => void }}
 */
export default function useRecentScreens() {
   const userId = useSelector((state) => state.auth.user?.id);
   const { flatItems } = useModuleCatalog();
   const { pathname } = useLocation();

   const byHref = useMemo(() => {
      const map = new Map();
      flatItems.forEach((item) => map.set(item.to, item));
      return map;
   }, [flatItems]);

   // `pathname` entra nas deps de propósito: ao voltar para a home depois de
   // visitar uma tela, o histórico recém-gravado precisa ser relido.
   const recents = useMemo(() => {
      if (!userId) return [];

      return readStored(userId)
         .sort((a, b) => (b.ts || 0) - (a.ts || 0))
         .map((entry) => byHref.get(entry.href)) // revalida contra permissões
         .filter(Boolean)
         .slice(0, MAX_SHOWN);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userId, byHref, pathname]);

   const clearRecents = useCallback(() => {
      writeStored(userId, []);
   }, [userId]);

   return { recents, clearRecents };
}
